import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../../utils/apiConfig';
import { ClipboardList, Loader2, IndianRupee, Calendar, Trophy, CreditCard } from 'lucide-react';
import { statusLabel, cAuth } from '../../components/events/eventUtils';
import CustomerFooter from '../../components/CustomerFooter';

const PAY_BADGE = { paid: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700', failed: 'bg-red-100 text-red-700', refunded: 'bg-purple-100 text-purple-700' };
const REG_BADGE = { confirmed: 'bg-emerald-100 text-emerald-700', waitlist: 'bg-amber-100 text-amber-700', cancelled: 'bg-red-100 text-red-700' };

export default function MyRegistrationsPage() {
  const navigate = useNavigate();
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const fetchRegs = () => {
    setLoading(true);
    axios.get(`${API}/events/my-registrations`, cAuth())
      .then((r) => setRegs(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(fetchRegs, []);

  const payNow = async (reg) => {
    setPaying(reg.id);
    try {
      const order = await axios.post(`${API}/events/payments/create-order`, { registration_id: reg.id }, cAuth());
      if (!order.data.success) { setPaying(null); return; }
      const { load } = await import('@cashfreepayments/cashfree-js');
      const cf = await load({ mode: 'production' });
      await cf.checkout({ paymentSessionId: order.data.payment_session_id, redirectTarget: '_self' });
    } catch (e) { setPaying(null); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-violet-50 to-fuchsia-50 pb-24">
      <div className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 px-5 pt-8 pb-10 text-white">
        <div className="flex items-center gap-2"><ClipboardList className="w-6 h-6" /><h1 className="text-2xl font-bold">My Registrations</h1></div>
        <p className="text-purple-100 text-sm mt-1">Your tournament entries & payment status</p>
      </div>

      <div className="px-4 -mt-5 space-y-3">
        {loading ? (
          <div className="text-center py-16 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
        ) : regs.length === 0 ? (
          <div className="bg-white rounded-2xl p-10 text-center text-slate-400 shadow-sm" data-testid="my-regs-empty">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p>No registrations yet.</p>
            <button onClick={() => navigate('/events')} className="mt-3 text-violet-600 font-semibold text-sm">Browse Tournaments →</button>
          </div>
        ) : regs.map((r) => (
          <div key={r.id} className="bg-white rounded-2xl shadow-md p-4" data-testid={`my-reg-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-bold text-slate-900">{r.tournament_name}</h3>
                <p className="text-xs font-mono text-slate-400">{r.registration_number}</p>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${REG_BADGE[r.status] || 'bg-slate-100'}`}>{statusLabel(r.status)}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${PAY_BADGE[r.payment_status] || 'bg-slate-100'}`}>{statusLabel(r.payment_status)}</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(r.categories || []).map((c, i) => <span key={i} className="text-xs px-2 py-0.5 rounded-md bg-violet-50 text-violet-700">{c.category_name}</span>)}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <span className="text-slate-800 font-bold flex items-center"><IndianRupee className="w-4 h-4" />{r.total_amount}</span>
              {r.payment_status === 'pending' && r.status !== 'cancelled' && r.total_amount > 0 && (
                <button onClick={() => payNow(r)} disabled={paying === r.id}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-sm font-semibold" data-testid={`pay-now-${r.id}`}>
                  {paying === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CreditCard className="w-4 h-4" /> Pay Now</>}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      <CustomerFooter />
    </div>
  );
}
